import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Container, Table, Badge } from 'react-bootstrap'
import { BsDownload } from 'react-icons/bs'
import * as XLSX from 'xlsx'
import { fetchBreakdownTickets, fetchBreakdownTicketsSummary } from '../../../store/actions/breakdownTickets'
import API from '../../../service/api'
import API_BOOK from '../../../service/endpoints'
import Loader from '../../helpers/hoc/loader/Loader'
import NoRecordFound from '../../helpers/hoc/noRecordFound'
import Pagination from '../../helpers/hoc/paginator/Pagination'

const ITEMS_PER_PAGE = 50

const BreakdownTicketsReport = () => {
  const dispatch = useDispatch()
  const tickets = useSelector(state => state.breakdownTickets.tickets)
  const totalTickets = useSelector(state => state.breakdownTickets.totalTickets)
  const summary = useSelector(state => state.breakdownTickets.summary)
  const [isLoading, setIsLoading] = useState(false)
  const [filterType, setFilterType] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      await Promise.all([
        dispatch(fetchBreakdownTickets({ pageIndex: 0, pageSize: ITEMS_PER_PAGE })),
        dispatch(fetchBreakdownTicketsSummary())
      ])
      setIsLoading(false)
    }
    loadData()
  }, [dispatch])

  useEffect(() => {
    if (currentPage > 1 || filterType !== 'all') {
      const loadPage = async () => {
        setIsLoading(true)
        await dispatch(fetchBreakdownTickets({
          type: filterType === 'all' ? undefined : filterType,
          pageIndex: currentPage - 1,
          pageSize: ITEMS_PER_PAGE
        }))
        setIsLoading(false)
      }
      loadPage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const handleFilterChange = async (type) => {
    setFilterType(type)
    setCurrentPage(1)
    setIsLoading(true)
    await dispatch(fetchBreakdownTickets({ type: type === 'all' ? undefined : type, pageIndex: 0, pageSize: ITEMS_PER_PAGE }))
    setIsLoading(false)
  }

  const exportToExcel = async () => {
    try {
      setIsLoading(true)
      const res = await API({
        url: API_BOOK.BREAKDOWN_TICKETS.LIST.url,
        method: API_BOOK.BREAKDOWN_TICKETS.LIST.method,
        data: { type: filterType === 'all' ? undefined : filterType }
      })
      const allTickets = res?.data?.data || []
      const data = allTickets.map((t, i) => ({
        'S.No': i + 1,
        'Ticket ID': t.ticketId,
        'Vehicle No.': t.registrationNumber,
        'Type': t.type,
        'Status': t.status,
        'Description': t.description,
        'Location': t.location,
        'Driver Name': t.driverName,
        'Driver Contact': t.driverContact,
        'Created Date': t.createdDate,
        'Closed Date': t.closedDate,
        'Remarks': t.remarks
      }))
      const ws = XLSX.utils.json_to_sheet(data)
      ws['!cols'] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length + 4, 16) }))
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Breakdown Tickets')
      const typeLabel = filterType === 'all' ? 'All' : filterType.charAt(0).toUpperCase() + filterType.slice(1)
      XLSX.writeFile(wb, `Breakdown_Tickets_${typeLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`)
    } catch (error) {
      console.log('Export error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Container fluid>
      <div className='py-3'>
        <div className='d-flex gap-3 mb-3 align-items-center'>
          <div className='d-flex gap-2'>
            <button
              className={`btn btn-sm ${filterType === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => handleFilterChange('all')}
            >
              All ({summary?.totalTickets || 0})
            </button>
            <button
              className={`btn btn-sm ${filterType === 'accident' ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => handleFilterChange('accident')}
            >
              Accident ({summary?.accidentTickets || 0})
            </button>
            <button
              className={`btn btn-sm ${filterType === 'breakdown' ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={() => handleFilterChange('breakdown')}
            >
              Breakdown ({summary?.breakdownTickets || 0})
            </button>
          </div>
          <div className='d-flex gap-3 ms-auto align-items-center'>
            <button
              className={!tickets?.length ? 'dwn-btn dwn-btn-disabled' : 'dwn-btn'}
              disabled={!tickets?.length}
              onClick={exportToExcel}
              title='Export to Excel'
            >
              <BsDownload style={{ width: '22px', height: '22px' }} />
            </button>
            <span className='text-muted' style={{ fontSize: '13px' }}>
              Accident Vehicles: <strong>{summary?.accidentVehicleCount || 0}</strong>
            </span>
            <span className='text-muted' style={{ fontSize: '13px' }}>
              Breakdown Vehicles: <strong>{summary?.breakdownVehicleCount || 0}</strong>
            </span>
          </div>
        </div>

        {!tickets?.length && !isLoading ? (
          <NoRecordFound />
        ) : (
          <div className='bg-white mb-2 position-relative' style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', overflowX: 'auto' }}>
          <Table responsive bordered hover size='sm' className='mb-0'>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr style={{ fontSize: '12px', background: '#EFF3F8', color: '#1F2A5D' }}>
                <th className='ps-2 text-nowrap'>S.No</th>
                <th className='ps-2 text-nowrap'>Ticket ID</th>
                <th className='ps-2 text-nowrap'>Vehicle No.</th>
                <th className='ps-2 text-nowrap'>Type</th>
                <th className='ps-2 text-nowrap'>Status</th>
                <th className='ps-2 text-nowrap'>Description</th>
                <th className='ps-2 text-nowrap'>Location</th>
                <th className='ps-2 text-nowrap'>Driver Name</th>
                <th className='ps-2 text-nowrap'>Driver Contact</th>
                <th className='ps-2 text-nowrap'>Created Date</th>
                <th className='ps-2 text-nowrap'>Closed Date</th>
                <th className='ps-2 text-nowrap'>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {tickets?.map((ticket, index) => (
                <tr key={ticket.ticketId || index} style={{ fontSize: '12px' }}>
                  <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                  <td>{ticket.ticketId}</td>
                  <td>{ticket.registrationNumber}</td>
                  <td>
                    <Badge bg={ticket.isAccident ? 'danger' : 'warning'} text={ticket.isAccident ? 'white' : 'dark'}>
                      {ticket.type}
                    </Badge>
                  </td>
                  <td>{ticket.status}</td>
                  <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ticket.description}</td>
                  <td>{ticket.location}</td>
                  <td>{ticket.driverName}</td>
                  <td>{ticket.driverContact}</td>
                  <td>{ticket.createdDate}</td>
                  <td>{ticket.closedDate}</td>
                  <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ticket.remarks}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          </div>
        )}
        {totalTickets > ITEMS_PER_PAGE && (
          <Pagination
            disabled={false}
            totalPages={Math.ceil(totalTickets / ITEMS_PER_PAGE)}
            currentPage={currentPage}
            setPage={setCurrentPage}
          />
        )}
      </div>
      <Loader isLoading={isLoading} />
    </Container>
  )
}

export default BreakdownTicketsReport
